import { useState } from "react";
import { useNavigate, Link } from "@tanstack/react-router";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { useAuthStore } from "@/stores/auth";
import { authApi, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const navigate = useNavigate();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { token, user } = await authApi.login(username, password);
      setAuth(token, user);
      navigate({ to: "/" });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "登录失败";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="grid min-h-screen bg-[#101827] lg:grid-cols-[minmax(420px,.9fr)_1.1fr]">
      <section className="flex flex-col justify-center bg-[#f8fafc] px-[8vw] py-[8vw]">
        <div className="flex items-center gap-3">
          <div className="grid h-[38px] w-[38px] place-items-center rounded-[10px] bg-[#2363eb] text-white shadow-[0_7px_20px_#2363eb33]">
            <ShieldCheck size={22} />
          </div>
          <div>
            <strong className="block text-[15px] tracking-[.01em] text-[#111b2e]">BizConsole</strong>
            <span className="mt-0.5 block text-[10px] tracking-[.1em] text-[#8490a3]">词元供应系统</span>
          </div>
        </div>
        <div className="mt-[68px] mb-7">
          <h1 className="m-0 text-[clamp(27px,3vw,38px)] font-semibold tracking-[-.03em] text-[#111b2e]">
            运营工作台
          </h1>
        </div>
        <form className="grid gap-4" onSubmit={onSubmit}>
          <div className="grid gap-2">
            <Label htmlFor="username" className="text-xs font-bold text-[#56647a]">
              用户名
            </Label>
            <Input
              id="username"
              name="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
              className="h-auto px-3 py-2.5"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password" className="text-xs font-bold text-[#56647a]">
              密码
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="h-auto px-3 py-2.5"
            />
          </div>
          {error && (
            <div className="rounded-md border border-[#f5c7c7] bg-[#fff2f1] px-3 py-2.5 text-xs text-[#b42318]">
              {error}
            </div>
          )}
          <Button type="submit" className="mt-1 w-full" disabled={loading}>
            {loading ? "登录中..." : "进入工作台"}
            <ChevronRight size={16} />
          </Button>
          <div className="mt-1 border-t border-[#e5eaf1] pt-3 text-center text-xs">
            供应商账号?{" "}
            <Link
              to="/supplier/login"
              className="font-semibold text-[#2363eb] underline-offset-4 hover:underline"
            >
              前往供应商端登录
            </Link>
          </div>
        </form>
      </section>
      <aside className="relative hidden flex-col justify-between overflow-hidden px-[8vw] py-[8vw] text-white lg:flex">
        <div
          className="absolute inset-0"
          style={{
            opacity: 0.22,
            backgroundImage:
              "linear-gradient(#7c92bd 1px, transparent 1px), linear-gradient(90deg, #7c92bd 1px, transparent 1px)",
            backgroundSize: "44px 44px",
            WebkitMaskImage: "linear-gradient(135deg, #000, transparent 72%)",
            maskImage: "linear-gradient(135deg, #000, transparent 72%)",
          }}
        />
        <div className="relative">
          <h2 className="mb-5 mt-8 text-[clamp(38px,5vw,64px)] leading-[1.04] tracking-[-.06em]">
            词元供应控制台
          </h2>
          <p className="m-0 max-w-[340px] text-[14px] leading-[1.8] text-[#93a1b9]">
            中转/推送站点、分发任务,一处掌控。
          </p>
        </div>
        <div className="relative text-[10px] tracking-[.12em] text-[#687791]">
          BizConsole · 词元分发
        </div>
      </aside>
    </main>
  );
}